import type { VendorBill } from '@/types'

/**
 * Vendor opening balance brought forward (F-MM-N24).
 *
 * A restaurant migrating onto Bhookly already owes its vendors. That balance
 * is recorded as ONE vendor bill flagged `is_opening_balance` — so "You owe",
 * Unpaid bills, Record payment, aging, the ledger and the statement all carry
 * it with no new logic. These helpers are the dialog's input rules and the
 * labels that keep such a bill from reading as "Bill from delivery".
 */

/** Matches the backend's ledger description exactly, so the row list, the
 *  on-screen ledger and the PDF statement all call it the same thing. */
export const OPENING_BALANCE_TITLE = 'Opening balance brought forward'

/** The vendor's live opening balance, if one was recorded. A voided one does
 *  not count — the backend lets it be replaced. */
export function findOpeningBalanceBill(bills: VendorBill[]): VendorBill | null {
  return bills.find(b => !!b.is_opening_balance && b.status !== 'void') ?? null
}

/** Title for a bill row: the vendor's invoice number, the settlement period
 *  a payout covers, the opening balance, or the delivery it was drafted from. */
export function billTitle(b: VendorBill): string {
  if (b.invoice_number) return b.invoice_number
  if (b.settlement_from && b.settlement_to) return `Payout for ${periodLabel(b.settlement_from, b.settlement_to)}`
  if (b.is_opening_balance) return OPENING_BALANCE_TITLE
  return 'Bill from delivery'
}

/** "1 to 31 Jul 2026", "25 Jul to 5 Aug 2026", "20 Dec 2025 to 5 Jan 2026" —
 *  the shortest form that is still unambiguous. Falls back to the raw values. */
function periodLabel(fromISO: string, toISO: string): string {
  const f = /^(\d{4})-(\d{2})-(\d{2})/.exec(fromISO)
  const t = /^(\d{4})-(\d{2})-(\d{2})/.exec(toISO)
  if (!f || !t) return `${fromISO} to ${toISO}`
  const [fy, fm, fd] = [f[1], Number(f[2]), Number(f[3])]
  const [ty, tm, td] = [t[1], Number(t[2]), Number(t[3])]
  if (fy === ty && fm === tm) return `${fd} to ${td} ${MONTHS[tm - 1]} ${ty}`
  if (fy === ty) return `${fd} ${MONTHS[fm - 1]} to ${td} ${MONTHS[tm - 1]} ${ty}`
  return `${fd} ${MONTHS[fm - 1]} ${fy} to ${td} ${MONTHS[tm - 1]} ${ty}`
}

/** Why a bill cannot be voided, or null when it can. Mirrors UpdateVendorBill
 *  (F-FE-N128): a settled or already-void bill is locked, and anything with a
 *  payment on it must keep its history — refund the payment first. */
export function voidBlockedReason(b: VendorBill): string | null {
  if (b.status === 'void') return 'Already void'
  if (b.status === 'paid') return 'Settled bills cannot be voided'
  if (b.amount_paid > 0.0001) return 'Something has been paid on it — a bill with payments cannot be voided'
  return null
}

export interface OpeningBalanceInput {
  amount: string
  as_of: string
  note?: string
}

export type OpeningBalanceParse =
  | { ok: true; payload: { amount: number; as_of: string; note?: string } }
  | { ok: false; reason: string }

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Validates the dialog's opening-balance fields. `today` is the local ISO
 *  date; a balance "as of" a future date is refused, as the backend does. */
export function parseOpeningBalanceInput(input: OpeningBalanceInput, today: string): OpeningBalanceParse {
  const amount = Number(input.amount)
  if (!input.amount.trim() || !Number.isFinite(amount) || amount <= 0) {
    return { ok: false, reason: 'Enter the amount you owed them — above zero' }
  }
  const as_of = input.as_of.trim()
  if (!ISO_DATE.test(as_of)) {
    return { ok: false, reason: 'Pick the date the balance is as of' }
  }
  if (as_of > today) {
    return { ok: false, reason: 'The opening balance date cannot be in the future' }
  }
  const note = (input.note ?? '').trim()
  return { ok: true, payload: note ? { amount, as_of, note } : { amount, as_of } }
}

/** Headline for a recorded opening balance: what it was, when, and what is
 *  still owed on it. */
export function describeOpeningBalance(
  b: VendorBill,
  fmt: (n: number) => string,
  fmtDate: (iso?: string | null) => string,
): { value: string; sub: string } {
  const value = `${fmt(b.amount)} as of ${fmtDate(b.bill_date)}`
  if (b.status === 'paid' || b.outstanding <= 0.01) return { value, sub: 'fully paid' }
  return { value, sub: `${fmt(b.outstanding)} still owed` }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "31 Aug 2026" for an ISO date; a dash for nothing; anything else as-is. */
export function shortDayMonthYear(iso?: string | null): string {
  if (!iso) return '—'
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`
}

/** Local calendar date as YYYY-MM-DD. Not `toISOString()`, which is UTC and
 *  turns a post-midnight Pakistan entry into yesterday's date. */
export function localISODate(d: Date = new Date()): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
